// fields/SelectSearchField.js
// ============================================
// Searchable select field with local data
// ============================================
import React, { useState, useEffect, useRef } from 'react';

export const SelectSearchField = ({ 
  field, 
  register, 
  errors, 
  theme, 
  setValue, 
  watch,
  options = [], // Array of { id, displayName, info }
  placeholder = "Search and select...",
  displayFormat = "full", // "full", "nameOnly", "custom"
  customDisplay = null, // Custom display function
  allowClear = true,
  maxDropdownHeight = "300px"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  const fieldTheme = theme.fields?.selectSearch || theme.fields?.select || {};
  const dropdownTheme = theme.dropdown || {};
  const currentValue = watch(field.id);
  
  // Initialize selected item from form value
  useEffect(() => {
    if (currentValue && options.length > 0) {
      const item = options.find(opt => opt.id === currentValue);
      if (item) {
        setSelectedItem(item);
        setSearchTerm('');
      }
    }
  }, [currentValue, options]);
  
  // Filter options based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(option => {
        const searchLower = searchTerm.toLowerCase();
        return (
          option.displayName.toLowerCase().includes(searchLower) ||
          (option.info && option.info.toLowerCase().includes(searchLower)) ||
          (option.id && option.id.toString().toLowerCase().includes(searchLower))
        );
      });
      setFilteredOptions(filtered);
      setHighlightedIndex(0);
    } else {
      setFilteredOptions(options);
      setHighlightedIndex(0);
    }
  }, [searchTerm, options]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }
    
    if (!isOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        scrollToHighlighted();
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        scrollToHighlighted();
        break;
        
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
        
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };
  
  const scrollToHighlighted = () => {
    setTimeout(() => {
      const highlighted = dropdownRef.current?.querySelector('[data-highlighted="true"]');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  };
  
  const handleSelect = (option) => {
    setSelectedItem(option);
    setValue(field.id, option.id, { shouldValidate: true });
    setSearchTerm('');
    setIsOpen(false);
    inputRef.current?.blur();
  };
  
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedItem(null);
    setValue(field.id, '', { shouldValidate: true });
    setSearchTerm('');
    setFilteredOptions(options);
  };
  
  const handleInputClick = () => {
    if (!selectedItem) {
      setIsOpen(true);
    } else if (!isOpen) {
      setIsOpen(true);
      setSearchTerm('');
    }
  };
  
  const getDisplayText = (item) => {
    if (!item) return '';
    
    if (customDisplay) {
      return customDisplay(item);
    }
    
    switch (displayFormat) {
      case 'nameOnly':
        return item.displayName;
      case 'full':
        return item.info ? `${item.displayName} - ${item.info}` : item.displayName;
      default:
        return item.displayName;
    }
  };
  
  const getInputClassName = () => {
    const classes = [
      fieldTheme.base || theme.input,
      'pr-10 cursor-pointer'
    ];
    
    if (isFocused) classes.push(fieldTheme.focus);
    if (errors[field.id]) classes.push(fieldTheme.error || theme.inputError);
    if (field.disabled) classes.push(fieldTheme.disabled);
    if (selectedItem) classes.push(fieldTheme.filled);
    
    return classes.join(' ');
  };
  
  // Helper function to get item classes
  const getItemClassName = (index, option) => {
    // Check if item is selected
    const isSelected = selectedItem?.id === option.id;
    
    // Check if item is highlighted
    const isHighlighted = index === highlightedIndex;
    
    // Determine which class to use
    if (isSelected && dropdownTheme.itemSelected) {
      return dropdownTheme.itemSelected;
    } else if (isHighlighted) {
      return dropdownTheme.itemHighlighted || 'px-3 py-2 cursor-pointer transition-colors bg-blue-50 dark:bg-blue-900/30';
    } else {
      return dropdownTheme.item || 'px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700';
    }
  };
  
  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        {/* Search Input */}
        <input
          ref={inputRef}
          type="text"
          value={selectedItem ? getDisplayText(selectedItem) : searchTerm}
          onChange={(e) => {
            if (!selectedItem) {
              setSearchTerm(e.target.value);
              if (!isOpen) setIsOpen(true);
            } else {
              // If item is selected, clear it and start new search
              setSelectedItem(null);
              setValue(field.id, '', { shouldValidate: false });
              setSearchTerm(e.target.value);
              setIsOpen(true);
            }
          }}
          onClick={handleInputClick}
          onFocus={() => {
            setIsFocused(true);
            if (!selectedItem) setIsOpen(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={selectedItem ? '' : placeholder}
          className={getInputClassName()}
          disabled={field.disabled}
          autoComplete="off"
        />
        
        {/* Icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {/* Clear button */}
          {allowClear && selectedItem && !field.disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          {/* Dropdown arrow */}
          <svg 
            className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {/* Dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className={dropdownTheme.container || 'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg overflow-auto'}
          style={{ maxHeight: maxDropdownHeight }}
        >
          {filteredOptions.length > 0 ? (
            <>
              {filteredOptions.map((option, index) => (
                <div
                  key={option.id}
                  data-highlighted={index === highlightedIndex}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={getItemClassName(index, option)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className={dropdownTheme.itemLabel || 'font-medium text-gray-900 dark:text-gray-100'}>
                        {option.displayName}
                      </div>
                      {option.info && (
                        <div className={dropdownTheme.itemInfo || 'text-sm text-gray-500 dark:text-gray-400 mt-0.5'}>
                          {option.info}
                        </div>
                      )}
                    </div>
                    {selectedItem?.id === option.id && (
                      <svg className={dropdownTheme.checkIcon || 'w-5 h-5 text-blue-600 dark:text-blue-400 ml-2 flex-shrink-0'} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className={dropdownTheme.noResultsText || 'px-3 py-2 text-gray-500 dark:text-gray-400 text-center'}>
              {searchTerm ? `No results found for "${searchTerm}"` : 'No options available'}
            </div>
          )}
        </div>
      )}
      
      {/* Hidden input for form value */}
      <input
        type="hidden"
        {...register(field.id, field.validation)}
      />
    </div>
  );
};